import axios from 'axios';
import { motivoDaSaida } from '../lib/sessao';

// Remove barra final se existir
const normalizeURL = (url) => url?.replace(/\/$/, '');

const API_BASE_URL = normalizeURL(process.env.REACT_APP_API_URL);

// Sem timeout, um backend pendurado = spinner eterno em qualquer tela (inventário §1.4, 28/09).
// 15 s para JSON (timeout não é repetido — lib/query.ts), upload (FormData) ganha 120 s.
// Chamada com timeout próprio (captacaoAPI.capturar) mantém o dela.
const TIMEOUT_PADRAO = 15_000;
const TIMEOUT_UPLOAD = 120_000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT_PADRAO,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Se o body é FormData, deixar o axios/browser definir o Content-Type com boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    if (config.timeout === TIMEOUT_PADRAO) config.timeout = TIMEOUT_UPLOAD;
  }
  return config;
});

// Interceptor para tratar erros e renovar token
api.interceptors.response.use(
  (response) => {
    const newToken = response.headers['x-new-token'];
    if (newToken) localStorage.setItem('token', newToken);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Não redirecionar se o erro for na própria rota de login
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      
      if (!isLoginRequest) {
        /**
         * POR QUE a sessão caiu. Sem isto, o 401 limpava o token e jogava pro /login MUDO: o Matheus
         * colou os cookies do Instagram, clicou em salvar, foi cuspido pra tela de login e concluiu
         * "deu erro ao salvar". Este sistema tem SESSÃO ÚNICA (`token_version` sobe a cada login), então
         * entrar em outro aparelho ou outra aba derruba a sessão aberta — e isso tem que estar escrito.
         * sessionStorage porque o motivo é desta aba e não deve sobreviver ao fechamento.
         * O motivo é lido ANTES de apagar o token (o `exp` dele diz se venceu). Antes todo 401 dizia
         * "usado em outro lugar", inclusive o token que só tinha vencido (B1, 28/09).
         */
        try {
          sessionStorage.setItem('motivo_saida', motivoDaSaida(error, localStorage.getItem('token')));
        } catch { /* aba privada pode bloquear storage; o redirect continua */ }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Disparar evento para sincronizar com outras abas e atualizar AuthContext
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'token',
          oldValue: 'removed',
          newValue: null,
          url: window.location.href
        }));
        
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 403) {
      console.warn('Acesso negado:', error.response?.data?.message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  check: () => api.get('/auth/check'),
};

// Removidas em 23/08 na revisão da onda 9: `listActiveSessions`, `getBrowserStatus` e
// `getSystemHealth` chamavam endpoints que NÃO existem no backend (404) e nenhuma tela as usava.
// Cliente de API com função morta apontando pra rota inexistente faz alguém achar que a feature
// existe — some quando se procura, e o 404 aparece só em produção.
// Status honesto do canal Meta: em vez de deixar o Embedded Signup falhar sem dizer o motivo, o
// backend diz exatamente qual env var falta (sem vazar valor — só chave/descrição/bloqueia).
export const metaAuthAPI = {
  status: () => api.get('/meta-auth/status'),
  // Troca o handle de uso único (da URL do callback OAuth) pelo access_token real — o token nunca
  // fica na query string (achado de segurança corrigido, missão dois-canais-whatsapp onda 8).
  resgatarToken: (handle) => api.post('/meta-auth/resgatar-token', { handle }),
};

export const instancesAPI = {
  /**
   * `semCache` furava o cache de 30s do servidor (a chave era a URL; uma query a mais = chave nova).
   * O B1.9-S REMOVEU esse cache — vazava a lista de um cliente para outro — então hoje o parâmetro é
   * inócuo: fica só porque chamadores antigos ainda o passam, e não faz mal.
   */
  list: (clienteId, { semCache = false } = {}) =>
    api.get(`/instancias-whatsapp?cliente_id=${clienteId}${semCache ? `&_=${Date.now()}` : ''}`),
  getUsage: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/usage`),
  getBusinessProfile: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/business-profile`),
  updateBusinessProfile: (instanceId, data) => api.patch(`/instancias-whatsapp/${instanceId}/business-profile`, data),
  updateDisplayName: (instanceId, displayName) => api.patch(`/instancias-whatsapp/${instanceId}/display-name`, { display_name: displayName }),
  uploadProfilePhoto: (instanceId, formData) => api.post(`/instancias-whatsapp/${instanceId}/profile-photo`, formData),
  create: (data) => api.post('/instancias-whatsapp/criar', data),
  getStatus: (clienteId) => api.get(`/instancias-whatsapp/status/${clienteId}`),
  getStatusReal: (clienteId) => api.get(`/instancias-whatsapp/status/${clienteId}`),
  updateConfiguracoes: (clienteId, data) => api.post(`/instancias-whatsapp/atualizar-configuracoes/${clienteId}`, data),
  
  // Meta API endpoints
  configureMetaApi: (clienteId, data) => api.post(`/instancias-whatsapp/configurar-meta-api/${clienteId}`, data),
  getMetaApiConfig: (clienteId) => api.get(`/instancias-whatsapp/meta-api-config/${clienteId}`),
  verifyConnection: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/verificar-conexao`),
  uploadCertificate: (instanceId, publicKey) => api.post(`/instancias-whatsapp/${instanceId}/upload-certificado`, { public_key: publicKey }),
  verifyCertificate: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/verificar-certificado`),
  
  // Gerenciamento de múltiplas instâncias
  setDefault: (instanceId, clienteId) => api.post(`/instancias-whatsapp/${instanceId}/definir-padrao?cliente_id=${clienteId}`),
  // Agente por chip: define persona/tom/oferta próprios deste número (agente_ativo liga/desliga).
  setAgente: (instanceId, data) => api.patch(`/instancias-whatsapp/${instanceId}/agente`, data),
  delete: (instanceId, clienteId) => api.delete(`/instancias-whatsapp/${instanceId}?cliente_id=${clienteId}`),
  configureMetaApiById: (instanceId, data) => api.post(`/instancias-whatsapp/${instanceId}/configurar-meta-api`, data),
  configurarZApi: (instanceId, data) => api.post(`/instancias-whatsapp/${instanceId}/configurar-z-api`, data),
  getZApiQrCode: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/z-api-qrcode`),
  disconnectZApi: (instanceId) => api.post(`/instancias-whatsapp/${instanceId}/z-api-disconnect`),
  setFluxo: (instanceId, fluxoId) => api.patch(`/instancias-whatsapp/${instanceId}/definir-fluxo`, { fluxo_id: fluxoId }),
  updateConfiguracoes: (instanceId, data) => api.patch(`/instancias-whatsapp/${instanceId}/configuracoes`, data),
  
  // Outros endpoints
  getAccountInfo: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/account-info`),
};

export const contactsAPI = {
  list: (clienteId, page = 1, limit = 50, search = '', status = '', temperature = '') => api.get(`/contatos/list/${clienteId}?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}${status ? `&status=${status}` : ''}${temperature ? `&temperature=${temperature}` : ''}`),
  autocomplete: (clienteId, query) => api.get(`/contatos/autocomplete/${clienteId}?q=${encodeURIComponent(query)}`),
  search: (clienteId) => api.get(`/contatos/list/${clienteId}?limit=10000`),
  create: (data) => api.post('/contatos', data),
  update: (clienteId, id, data) => api.patch(`/contatos/${clienteId}/${id}`, data),
  delete: (clienteId, id) => api.delete(`/contatos/${clienteId}/${id}`),
  get: (clienteId, id) => api.get(`/contatos/${clienteId}/${id}`),
  getOne: (clienteId, id) => api.get(`/contatos/${clienteId}/${id}`),
  importCSV: (data) => api.post('/contatos/import-csv', data),
  addCanal: (id, data) => api.post(`/contatos/${id}/canais`, data),
  removeCanal: (canalId) => api.delete(`/contatos/canais/${canalId}`),
  getByTelefone: (clienteId, telefone) => api.get(`/contatos/por-telefone/${clienteId}/${telefone}`),
  addComentario: (id, data) => api.post(`/contatos/${id}/comentarios`, data),
  getComentarios: (id) => api.get(`/contatos/${id}/comentarios`),
  toggleOptIn: (id, optIn) => api.patch(`/contatos/${id}/opt-in`, { opt_in_ativo: optIn }),
};

export const groupsAPI = {
  list: (clienteId) => api.get(`/grupos${clienteId ? `?cliente_id=${clienteId}` : ''}`),
  get: (id) => api.get(`/grupos/${id}`),
  create: (data) => api.post('/grupos', data),
  update: (id, data) => api.patch(`/grupos/${id}`, data),
  delete: (id) => api.delete(`/grupos/${id}`),
  addContact: (data) => api.post('/grupos/contato', Array.isArray(data) ? data : [data]),
  removeContact: (id) => api.delete(`/grupos/contato/${id}`),
  getContacts: (groupId) => api.get(`/grupos/${groupId}/contatos`),
  importCSV: (groupId, data) => api.post(`/grupos/${groupId}/import-csv`, data),
};

export const waGroupsAPI = {
  list: (clienteId, params) => api.get('/grupos-whatsapp', { params: { cliente_id: clienteId, ...params } }),
  get: (id, clienteId) => api.get(`/grupos-whatsapp/${id}`, { params: { cliente_id: clienteId } }),
  resumo: (clienteId) => api.get('/grupos-whatsapp/resumo', { params: { cliente_id: clienteId } }),
  sync: (instanceId, clienteId) => api.post('/grupos-whatsapp/sync', { instancia_id: instanceId, cliente_id: clienteId }),
  create: (data) => api.post('/grupos-whatsapp/create', data),
  getEngagement: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/engagement`, { params: { cliente_id: clienteId, ...params } }),
  getTemperature: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/temperature`, { params: { cliente_id: clienteId, ...params } }),
  getInsights: (id, clienteId) => api.get(`/grupos-whatsapp/${id}/insights`, { params: { cliente_id: clienteId } }),
  getWordCloud: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/wordcloud`, { params: { cliente_id: clienteId, ...params } }),
  getHeatmap: (clienteId, params) => api.get('/grupos-whatsapp/heatmap/data', { params: { cliente_id: clienteId, ...params } }),
  getAnalytics: (clienteId, params) => api.get('/conversas/grupo/analytics', { params: { cliente_id: clienteId, ...params } }),
  // Categorias
  getCategorias: () => api.get('/grupo-categorias'),
  createCategoria: (data) => api.post('/grupo-categorias', data),
  updateCategoria: (id, data) => api.put(`/grupo-categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/grupo-categorias/${id}`),
  createOpcao: (categoriaId, data) => api.post(`/grupo-categorias/${categoriaId}/opcoes`, data),
  updateOpcao: (id, data) => api.put(`/grupo-categorias/opcoes/${id}`, data),
  deleteOpcao: (id) => api.delete(`/grupo-categorias/opcoes/${id}`),
  // Atribuição de categorias ao grupo
  setGrupoCategorias: (grupoId, valores) => api.put(`/grupos-whatsapp/${grupoId}/categorias`, { valores }),
  getGrupoCategorias: (grupoId) => api.get(`/grupos-whatsapp/${grupoId}/categorias`),
  // Responsável e meta
  setResponsavel: (grupoId, contatoId) => api.put(`/grupos-whatsapp/${grupoId}/responsavel`, { contato_id: contatoId }),
  setMeta: (grupoId, meta) => api.put(`/grupos-whatsapp/${grupoId}/meta`, { meta_participantes: meta }),
  getMetaGeral: (clienteId) => api.get(`/grupos-whatsapp/meta-geral/${clienteId}`),
};

export const campaignsAPI = {
  list: () => api.get('/campanhas'),
  get: (id) => api.get(`/campanhas/${id}`),
  create: (data) => api.post('/campanhas', data),
  update: (id, data) => api.patch(`/campanhas/${id}`, data),
  delete: (id) => api.delete(`/campanhas/${id}`),
  execute: (id) => api.post(`/campanhas/${id}/execute`),
  duplicar: (id) => api.post(`/campanhas/${id}/duplicar`),
  getMessages: (id) => api.get(`/campanhas/${id}/messages`),
  getMediaUrl: (mediaId) => api.get(`/campanhas/media-url/${mediaId}`),
  addMessage: (id, data) => api.post(`/campanhas/${id}/messages`, data),
  updateMessage: (id, messageId, data) => api.patch(`/campanhas/${id}/messages/${messageId}`, data),
  removeMessage: (id, messageId) => api.delete(`/campanhas/${id}/messages/${messageId}`),
  getStats: (id) => api.get(`/campanhas/${id}/stats`),
  getRecipients: (id, params) => api.get(`/campanhas/${id}/recipients`, { params }),
  clearHistory: (id) => api.delete(`/campanhas/${id}/history`),
  getPendentes: (id) => api.get(`/campanhas/${id}/contatos-pendentes`),
  getCusto: (id) => api.get(`/campanhas/${id}/custo`),
  reenviarPendentes: (id) => api.post(`/campanhas/${id}/reenviar-pendentes`),
  submeterTemplate: (id, data) => api.post(`/campanhas/${id}/submeter-template`, data),
  verificarStatusTemplate: (id) => api.get(`/campanhas/${id}/status-template`),
  uploadMedia: (usuarioId, formData) => api.post(`/campanhas/upload-media/${usuarioId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const messagesAPI = {
  send: (data) => api.post('/mensagens', data),
  schedule: (data) => api.post('/mensagens/schedule', data),
  getByCliente: () => api.get('/mensagens/cliente'),
  getByGroup: (groupId) => api.get(`/grupos/${groupId}`),
  getVariables: () => api.get('/mensagens/variaveis'),
};

export const autoRulesAPI = {
  list: (clienteId) => api.get(`/regras-automaticas/cliente/${clienteId}`),
  create: (data) => api.post('/regras-automaticas', data),
  update: (id, data) => api.patch(`/regras-automaticas/${id}`, data),
  delete: (id) => api.delete(`/regras-automaticas/${id}`),
};

// Fila de Hoje — cockpit diário (clienteId vem do token no backend).
export const filaHojeAPI = {
  hoje: () => api.get('/fila-hoje'),
};

// O dono manda o rascunho de uma tarefa "IA travou" em 1 toque (ADR-002). `texto` vazio = o rascunho
// da tarefa, que o backend extrai do bloco (o front nunca recorta o draft_message).
export const automacaoAPI = {
  enviarRascunho: (id, texto) => api.patch(`/automacao/tarefas/${id}/enviar-rascunho`, { texto }),
};

export const iaAPI = {
  getConfig: (clienteId) => api.get(`/ia-assistant/config/cliente/${clienteId}`),
  listConfigs: (clienteId) => api.get(`/ia-assistant/list/cliente/${clienteId}`),
  createConfig: (data) => api.post('/ia-assistant', data),
  updateConfig: (clienteId, data) => api.patch(`/ia-assistant/${clienteId}`, data),
  testResponse: (clienteId, data) => api.post(`/ia-assistant/test/${clienteId}`, data),
  deleteConfig: (id) => api.delete(`/ia-assistant/${id}`),
};

export const fluxosAPI = {
  list: (clienteId) => api.get(`/fluxos-conversa/cliente/${clienteId}`),
  create: (clienteId, data) => api.post(`/fluxos-conversa/cliente/${clienteId}`, data),
  get: (id) => api.get(`/fluxos-conversa/${id}`),
  update: (id, data) => api.put(`/fluxos-conversa/${id}`, data),
  delete: (id) => api.delete(`/fluxos-conversa/${id}`),
  getEtapas: (id) => api.get(`/fluxos-conversa/${id}/etapas`),
  getFluxoAtivo: (clienteId) => api.get(`/fluxos-conversa/cliente/${clienteId}/ativo`),
  listar: (clienteId) => api.get(`/fluxos-conversa/cliente/${clienteId}`),
  criar: (data) => api.post(`/fluxos-conversa/cliente/${data.cliente_id}`, data),
  atualizar: (id, data) => api.put(`/fluxos-conversa/${id}`, data),
  deletar: (id) => api.delete(`/fluxos-conversa/${id}`),
  duplicar: (id) => api.post(`/fluxos-conversa/${id}/duplicar`),
};

export const tagsAPI = {
  list: (clienteId) => api.get(`/tags-atendimento?cliente_id=${clienteId}`),
  create: (data) => api.post('/tags-atendimento', data),
  update: (id, data) => api.patch(`/tags-atendimento/${id}`, data),
  getConversas: (tagId) => api.get(`/tags-atendimento/conversas?tag_id=${tagId}`),
};

export const templatesMetaAPI = {
  list: () => api.get('/templates-meta'),
  sync: (instanciaId) => api.post(`/templates-meta/sync/${instanciaId}`),
  get: (id) => api.get(`/templates-meta/${id}`),
  create: (data) => api.post('/templates-meta', data),
  update: (id, data) => api.patch(`/templates-meta/${id}`, data),
  delete: (id) => api.delete(`/templates-meta/${id}`),
  submeter: (id) => api.post(`/templates-meta/${id}/submeter`),
  verificarStatus: (id) => api.get(`/templates-meta/${id}/verificar-status`),
};

export const sessionsAPI = {
  pauseIA: (data) => api.post('/sessoes-atendimento/pausar-ia', data),
  reactivateIA: (data) => api.post('/sessoes-atendimento/reativar-ia', data),
  getStatus: (clienteId, usuarioId, telefone) => 
    api.get(`/sessoes-atendimento/status/${clienteId}/${usuarioId}/${telefone}`),
  getConfig: (clienteId) => api.get(`/sessoes-atendimento/config/${clienteId}`),
  updateConfig: (clienteId, data) => api.patch(`/sessoes-atendimento/config/${clienteId}`, data),
  markHuman: (data) => api.post('/sessoes-atendimento/marcar-humano', data),
};

// Novos endpoints para monitoramento e controle avançado
export const monitoringAPI = {
  getInstanceDiagnostic: (clienteId) => instancesAPI.diagnostico(clienteId),
  autoFix: (clienteId) => instancesAPI.corrigirAutomatico(clienteId),
};

export const atendimentoAPI = {
  /**
   * A fila de quem espera humano. Apontava para `/atendimento/conversas/` — TODAS as conversas, não
   * as pendentes — e nenhuma tela chamava. Órfão e mal apontado ao mesmo tempo.
   *
   * Importa mais do que parece: desde 31/08 a IA passa a conversa para humano quando a guarda de
   * saída reprova a resposta. Sem tela mostrando essa fila, o handoff vira outro silêncio — e
   * corrigir um silêncio criando outro é o erro que a varredura existe para impedir.
   */
  getConversasPendentes: (clienteId) => api.get(`/atendimento/conversas-pendentes/${clienteId}`),
  pausarIA: (data) => api.post('/atendimento/pausar-ia', data),
  reativarIA: (data) => api.post('/atendimento/reativar-ia', data),
  verificarSessao: (telefone, clienteId) => api.get(`/atendimento/sessao/${telefone}/${clienteId}`),
};

export const usuariosAPI = {
  list: () => api.get('/usuarios'),
  listMinhaConta: () => api.get('/usuarios/minha-conta'),
  get: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.patch(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
  alterarNivelAcesso: (data) => api.patch('/usuarios/alterar-nivel-acesso', data),
};

export const niveisAcessoAPI = {
  getTodos: () => api.get('/nivel-acesso/todos'),
  getDescricoes: () => api.get('/nivel-acesso/descricoes'),
};

export const clientesAPI = {
  list: () => api.get('/clientes'),
  get: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (data) => api.patch('/clientes', data),
  delete: (id) => api.delete(`/clientes/${id}`),
};

export const mediaAPI = {
  getSignedUrl: (s3Url) => api.get('/media/signed-url', { params: { url: s3Url } }),
  upload: (clienteId, usuarioId, formData) => api.post(`/media/cliente/${clienteId}/${usuarioId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: (clienteId) => api.get(`/media/list/${clienteId}`),
};

export const zApiAdminAPI = {
  listar: () => api.get('/admin/z-api/instancias'),
  listarDisponiveis: () => api.get('/admin/z-api/instancias/disponiveis'),
  criar: (data) => api.post('/admin/z-api/instancias', data),
  assinar: (id) => api.post(`/admin/z-api/instancias/${id}/assinar`),
};

// Utilitários para facilitar o uso
export const whatsappUtils = {
  // Verificar se instância está completamente funcional
  isFullyOperational: (instance) => {
    return instance?.status === 'conectada' && 
           instance?.numero_whatsapp && 
           instance?.ia_config_id && 
           instance?.listener?.ativo;
  },
  
  // Obter status resumido
  getStatusSummary: (instance) => {
    if (!instance) return { status: 'not_found', message: 'Instância não encontrada' };
    
    if (whatsappUtils.isFullyOperational(instance)) {
      return { status: 'operational', message: 'Sistema funcionando completamente' };
    }
    
    const issues = [];
    if (instance.status !== 'conectada') issues.push('WhatsApp desconectado');
    if (!instance.numero_whatsapp) issues.push('Número não identificado');
    if (!instance.ia_config_id) issues.push('IA não configurada');
    if (!instance.listener?.ativo) issues.push('Listener inativo');
    
    return { 
      status: 'partial', 
      message: `Problemas: ${issues.join(', ')}`,
      issues 
    };
  },
  
  // Formatar número WhatsApp
  formatPhoneNumber: (number) => {
    if (!number) return 'N/A';
    return number.replace(/^55/, '+55 ');
  }
};

export const analyticsAPI = {
  // clienteId vem do token no backend — não passa na URL.
  getContaDaMeta: (params) => api.get('/analytics/conta-da-meta', { params }),
  getFunilLeads: () => api.get('/analytics/funil-leads'),
  getReceita: () => api.get('/analytics/receita'),
  getFunilSemanal: (semanas = 12) => api.get('/analytics/funil-semanal', { params: { semanas } }),

  // A11: `intervalo` ({ dataInicio, dataFim }) faz o resumo ler a MESMA janela do getPeriodo.
  getResumo: (clienteId, dias = 30, intervalo) =>
    api.get(`/analytics/resumo/${clienteId}`, { params: { dias, ...(intervalo || {}) } }),
  
  getPeriodo: (clienteId, params) => 
    api.get(`/analytics/periodo/${clienteId}`, { params }),
  
  getComparativo: (clienteId, instancias, dias = 30) => 
    api.get(`/analytics/comparativo/${clienteId}?instancias=${instancias.join(',')}&dias=${dias}`),
  
  calcularManual: (instanciaId, data) => 
    api.get(`/analytics/calcular/${instanciaId}${data ? `?data=${data}` : ''}`),

  getConversasSentimento: (clienteId, params) =>
    api.get(`/analytics/sentimento/conversas/${clienteId}`, { params }),

  getPalavrasChave: (clienteId, params) =>
    api.get(`/analytics/palavras-chave/${clienteId}`, { params }),

  getContatosAtivos: (clienteId, params) =>
    api.get(`/analytics/contatos-ativos/${clienteId}`, { params }),

  getHorariosPico: (clienteId, params) =>
    api.get(`/analytics/horarios-pico/${clienteId}`, { params }),

  getTempoRespostaInstancias: (clienteId, params) =>
    api.get(`/analytics/tempo-resposta-instancias/${clienteId}`, { params }),

  getInstagramInsights: (clienteId, params = {}) =>
    api.get(`/analytics/instagram/${clienteId}`, { params }),

  getFacebookInsights: (clienteId, params = {}) =>
    api.get(`/analytics/facebook/${clienteId}`, { params }),
};

export const instagramPostsAPI = {
  getAll: (clienteId, instanciaId) =>
    api.get('/instagram-posts', { params: { cliente_id: clienteId, instancia_id: instanciaId } }),
  getOne: (id) => api.get(`/instagram-posts/${id}`),
  getDetails: (id) => api.get(`/instagram-posts/${id}/details`),
  create: (data) => api.post('/instagram-posts', data),
  update: (id, data) => api.put(`/instagram-posts/${id}`, data),
  remove: (id) => api.delete(`/instagram-posts/${id}`),
  upload: (formData) => api.post('/instagram-posts/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  publishNow: (id) => api.post(`/instagram-posts/${id}/publish-now`),
  importPosts: (instanciaId, clienteId) => api.post(`/instagram-posts/import/${instanciaId}`, { cliente_id: clienteId }),
  importSingle: (mediaId, platform, pageId) => api.post('/instagram-posts/import-single', { mediaId, platform, pageId }),
  // Comentários
  importComments: (id) => api.post(`/instagram-posts/${id}/comments/import`),
  importAllComments: (clienteId) => api.post(`/instagram-posts/comments/import-all`, { cliente_id: clienteId }),
  importFacebookComments: (clienteId) => api.post(`/instagram-posts/comments/import-facebook/${clienteId}`),
  getComments: (id) => api.get(`/instagram-posts/${id}/comments`),
  analyzePost: (id) => api.post(`/instagram-posts/${id}/analyze`),
  getTopCommenters: (clienteId, platform) => 
    api.get(`/instagram-posts/comments/top-commenters/${clienteId}${platform ? `?platform=${platform}` : ''}`),
  getTopCommentersConsolidated: (clienteId) => 
    api.get(`/instagram-posts/comments/top-commenters-consolidated/${clienteId}`),
  getUserComments: (clienteId, username, platform) => 
    api.get(`/instagram-posts/comments/user/${clienteId}/${username}${platform ? `?platform=${platform}` : ''}`),
};

export const cobrancaAPI = {
  getCotacao: () => api.get('/cobranca/cotacao'),
  getConfig: (clienteId) => api.get(`/cobranca/configuracoes/${clienteId}`),
  getAllConfigs: () => api.get('/cobranca/configuracoes'),
  saveConfig: (clienteId, data) => api.post(`/cobranca/configuracoes/${clienteId}`, data),
  deleteConfig: (id) => api.delete(`/cobranca/configuracoes/${id}`),
  calcular: (clienteId, totalContatos, tipo = 'marketing') =>
    api.get(`/cobranca/calcular?clienteId=${clienteId}&totalContatos=${totalContatos}&tipo=${tipo}`),
};

export const faturamentoAPI = {
  getResumo: (clienteId, mes) => api.get(`/faturamento/resumo?cliente_id=${clienteId}${mes ? `&mes=${mes}` : ''}`),
  getHistorico: (clienteId, meses = 6) => api.get(`/faturamento/historico?cliente_id=${clienteId}&meses=${meses}`),
  getFaturas: (clienteId) => api.get(`/faturamento/faturas?cliente_id=${clienteId}`),
  getCicloAtual: (clienteId) => api.get(`/faturamento/ciclo-atual?cliente_id=${clienteId}`),
  downloadExtratoPdf: (clienteId) => api.get(`/faturamento/extrato-pdf?cliente_id=${clienteId}`, { responseType: 'blob' }),
};

export const walletAPI = {
  getSaldo: (clienteId) => api.get(`/wallet/saldo/${clienteId}`),
  getExtrato: (clienteId, limit = 20, page = 1) => api.get(`/wallet/extrato/${clienteId}?limit=${limit}&page=${page}`),
  creditar: (data) => api.post('/wallet/creditar', data),
  debitar: (data) => api.post('/wallet/debitar', data),
  estorno: (data) => api.post('/wallet/estorno', data),
  verificar: (clienteId, valor) => api.get(`/wallet/verificar/${clienteId}?valor=${valor}`),
};

export const walletClienteAPI = {
  getSaldo: (clienteId) => api.get(`/wallet-cliente/saldo/${clienteId}`),
  getExtrato: (clienteId, limit = 20, page = 1) => api.get(`/wallet-cliente/extrato/${clienteId}?limit=${limit}&page=${page}`),
  criarRecarga: (data) => api.post('/wallet-cliente/recarga', data),
  listarCobrancas: (clienteId) => api.get(`/wallet-cliente/cobrancas/${clienteId}`),
  consultarCobranca: (id) => api.get(`/wallet-cliente/cobranca/${id}`),
  downloadPdf: (codigoSolicitacao) => api.get(`/inter-pix/cobranca/${codigoSolicitacao}/pdf`, { responseType: 'blob' }),
  upgrade: (planoId) => api.post('/planos/upgrade', { plano_id: planoId }),
};

export const concorrentesAPI = {
  list: () => api.get('/concorrentes'),
  create: (data) => api.post('/concorrentes', data),
  get: (id) => api.get(`/concorrentes/${id}`),
  delete: (id) => api.delete(`/concorrentes/${id}`),
  analisar: (id, accessToken, days) => api.post(`/concorrentes/${id}/analisar`, { access_token: accessToken, days }),
  historico: (id) => api.get(`/concorrentes/${id}/historico`),
  trends: (days) => api.get(`/concorrentes/trends${days ? `?days=${days}` : ''}`),
  gerarIdeias: (days, tipos) => api.post(`/concorrentes/gerar-ideias?${days ? `days=${days}` : ''}${tipos ? `&tipos=${tipos}` : ''}`),
  expandirIdeia: (ideia, tom) => api.post('/concorrentes/expandir-ideia', { ideia, tom }),
  gerarCalendario: (days, tipos) => api.post(`/concorrentes/gerar-calendario?${days ? `days=${days}` : ''}${tipos ? `&tipos=${tipos}` : ''}`),
  listRoteiros: () => api.get('/concorrentes/roteiros'),
  getRoteiro: (id) => api.get(`/concorrentes/roteiros/${id}`),
  updateRoteiro: (id, data) => api.put(`/concorrentes/roteiros/${id}`, data),
  deleteRoteiro: (id) => api.delete(`/concorrentes/roteiros/${id}`),
};

export const notificacoesAPI = {
  listar: (page = 1, limit = 20) => api.get(`/notificacoes?page=${page}&limit=${limit}`),
  contar: () => api.get('/notificacoes/count'),
  marcarLida: (id) => api.patch(`/notificacoes/${id}/lida`),
  marcarTodasLidas: () => api.patch('/notificacoes/marcar-todas-lidas'),
  marcarLidaPorContato: (telefone) => api.patch(`/notificacoes/contato/${encodeURIComponent(telefone)}/lida`),
};

export const commentsAPI = {
  reply: (commentId, postId, message) => api.post(`/instagram-posts/comments/${commentId}/reply`, { message, postId }),
};

export const cadastroAPI = {
  verificar: (clienteId) => api.get(`/clientes/cadastro-completo/${clienteId}`),
  completar: (clienteId, data) => api.patch(`/clientes/completar-cadastro/${clienteId}`, data),
  onboarding: (clienteId) => api.get(`/clientes/onboarding/${clienteId}`),
};

export const emailAPI = {
  resendConfirmation: () => api.post('/auth/resend-confirmation'),
};

export const integracoesAPI = {
  getByCliente: (clienteId) => api.get(`/integracoes/cliente/${clienteId}`),
  criar: (data) => api.post('/integracoes', data),
  sync: (id) => api.post(`/integracoes/${id}/sync`),
  testar: (id) => api.post(`/integracoes/${id}/testar`),
  deletarIntegracao: (id) => api.post(`/integracoes/${id}/deletar`),
  autorizarBling: (clienteId, nome) => api.post('/integracoes/bling/authorize', { cliente_id: clienteId, nome }),
  // Pedidos
  listar: () => api.get('/pedidos'),
  pedidos: (telefone) => api.get(`/pedidos/telefone/${telefone}`),
  atualizarStatusPedido: (pedidoId, status) => api.patch(`/pedidos/${pedidoId}/status`, { status }),
  cancelarPedido: (pedidoId) => api.post(`/pedidos/${pedidoId}/cancelar`),
  editarPedido: (pedidoId, dados) => api.patch(`/pedidos/${pedidoId}`, dados),
  // Produtos
  buscarProduto: (q) => api.get(`/produtos/busca?q=${encodeURIComponent(q)}`),
  listarProdutos: (params) => api.get('/produtos', { params }),
  categorias: () => api.get('/produtos/categorias'),
  statsProdutos: () => api.get('/produtos/stats'),
  atualizarCategoria: (id, categoria) => api.patch(`/produtos/${id}/categoria`, { categoria }),
};

export const vendedoresAPI = {
  list: () => api.get('/admin/vendedores'),
  get: (id) => api.get(`/admin/vendedores/${id}`),
  stats: (id, params) => api.get(`/admin/vendedores/${id}/stats`, { params }),
  create: (data) => api.post('/admin/vendedores', data),
  update: (id, data) => api.patch(`/admin/vendedores/${id}`, data),
  remove: (id) => api.delete(`/admin/vendedores/${id}`),
};

export const leadsAPI = {
  list: (params) => api.get('/admin/leads', { params }),
  resumo: (params) => api.get('/admin/leads/resumo', { params }),
};


// Gateway WhatsApp próprio (Baileys) — conectar número via QR, sem depender da Meta.
export const gatewayAPI = {
  criar: (cliente_id, usuario_id, nome) =>
    api.post('/gateway/instancias', { cliente_id, usuario_id, nome }),
  connect: (id) => api.post(`/gateway/instancias/${id}/connect`),
  qr: (id) => api.get(`/gateway/instancias/${id}/qr`),
  status: (id) => api.get(`/gateway/instancias/${id}/status`),
  send: (id, to, text) => api.post(`/gateway/instancias/${id}/send`, { to, text }),
  desconectar: (id) => api.delete(`/gateway/instancias/${id}`),
};

// Cérebro de chips (pool + aquecimento + distribuição anti-ban).
export const chipsAPI = {
  // A cadeia de 11 elos, a prévia (o que vai acontecer) e o ledger (o que aconteceu).
  cadeia: () => api.get('/chips/cadeia'),
  previa: () => api.get('/chips/previa'),
  ledger: (horas = 24) => api.get(`/chips/ledger?horas=${horas}`),
  estado: () => api.get('/chips/estado'),
  config: () => api.get('/chips/config'),
  // O CONTRATO de cada knob (default, descrição, grupo, se foi gravado). A tela de Configuração
  // renderiza a partir daqui: knob novo no CONFIG_SPEC aparece sozinho, nunca nasce escondido.
  configSpec: () => api.get('/chips/config/spec'),
  // Onda 14: o mapa do aquecimento (escada, protocolo, onde cada chip está, regras com procedência).
  aquecimento: () => api.get('/chips/aquecimento'),
  setConfig: (chave, valor) => api.post('/chips/config', { chave, valor }),
  // Teto manual de cota por chip — só reduz a curva (freio), null limpa.
  cotaManual: (instanciaId, valor) => api.post(`/chips/${instanciaId}/cota-manual`, { valor }),
  // `desde` (AAAA-MM-DD) declara o aquecimento que já aconteceu no celular antes do pareamento.
  aquecer: (id, desde) => api.post(`/chips/${id}/aquecer`, desde ? { desde } : {}),
  // Checklist de onboarding de um número (ADR-M3-002): 6 itens, 4 respondidos pelo sistema.
  onboardingChip: (id) => api.get(`/chips/${id}/onboarding`),
  confirmaOnboardingChip: (id, item) => api.post(`/chips/${id}/onboarding/confirma`, { item }),
  /**
   * FURA O GATE DO DIA 14 por decisão explícita do dono. `confirmo: true` é obrigatório (o backend
   * recusa sem ele), o motivo fica gravado e a liberação EXPIRA sozinha na data — nunca "pra sempre".
   */
  liberarFrioHoje: (id, { motivo, dias } = {}) =>
    api.post(`/chips/${id}/liberar-frio-hoje`, { confirmo: true, motivo, dias }),
  cancelarFrioLiberado: (id) => api.post(`/chips/${id}/cancelar-frio-liberado`),
  /**
   * A cota de HOJE, à mão — a única coisa que pode AUMENTAR o teto de aberturas frias. Vale só hoje;
   * amanhã a escada volta a mandar sozinha.
   */
  cotaDeHoje: (id, { valor, motivo, zerarConsumo } = {}) =>
    api.post(`/chips/${id}/cota-de-hoje`, { confirmo: true, valor, motivo, zerarConsumo }),
  // Escada de volume (ADR-006): o sistema PROPÕE o degrau, o dono confirma. Subir só no degrau
  // proposto (que exige entrega e resposta acima do piso); descer é sempre aceito, porque é freio.
  escada: (id) => api.get(`/chips/${id}/escada`),
  /** Ensaio no próprio número: passa pelo caminho REAL, mas não conta como abertura fria. */
  enviarTeste: (id, telefone, texto) => api.post(`/chips/${id}/enviar-teste`, { telefone, texto }),
  confirmarDegrau: (id, degrau) => api.post(`/chips/${id}/degrau`, { degrau }),
  saude: (id, estado, motivo) => api.post(`/chips/${id}/saude`, { estado, motivo }),
  // Detalhe de UM chip: estado + potencial (quanto aguenta hoje) + série de cota.
  detalhe: (id) => api.get(`/chips/${id}/detalhe`),
  importarAlvos: (cliente_id, usuario_id, alvos) => api.post('/chips/alvos', { cliente_id, usuario_id, alvos }),
  listarAlvos: (estado) => api.get('/chips/alvos', { params: estado ? { estado } : {} }),
  tickDry: () => api.post('/chips/tick-dry'),
  // Cota de conversas novas lida DO SERVIDOR (leitura pura, não envia nada).
  cota: () => api.get('/chips/cota'),
  medirCota: () => api.post('/chips/medir-cota'),
};

// Captação de leads do Instagram → fila de disparo.
export const captacaoAPI = {
  /** Estado do token do backend do produto. O valor NUNCA volta — só se existe e o tamanho. */
  onboardingConfig: () => api.get('/captacao/onboarding/config'),
  salvarOnboarding: (admin_token, base_url) => api.post('/captacao/onboarding/config', { admin_token, base_url }),
  // "Virou trial" → cria a loja no produto. `target:'prod'` exige confirmProd (conta real + e-mail).
  criarLoja: (leadId, dados) => api.post(`/captacao/leads/${leadId}/criar-loja`, dados),
  // Captação como JOB (onda 4): dispara e acompanha por polling; `latest` pega a última coleta.
  criarJob: (dados) => api.post('/captacao/jobs', dados),
  statusJob: (id = 'latest') => api.get(`/captacao/jobs/${id}`),
  cancelarJob: (id) => api.post(`/captacao/jobs/${id}/cancelar`, {}),
  // A conta-isca se troca pelo `setSessao` que já existia abaixo — o que faltava era a TELA.
  sessao: () => api.get('/captacao/sessao'),
  setSessao: (sessionid, csrftoken, ds_user_id) => api.post('/captacao/sessao', { sessionid, csrftoken, ds_user_id }),
  leads: (cliente_id, estado) => api.get('/captacao/leads', { params: { cliente_id, ...(estado ? { estado } : {}) } }),
  capturar: (cliente_id, isca, meta) => api.post('/captacao/capturar', { cliente_id, isca, meta }, { timeout: 300000 }),
  enviarFila: (cliente_id, usuario_id, mensagem, leadIds) => api.post('/captacao/enviar-fila', { cliente_id, usuario_id, mensagem, leadIds }),
  // "Virou cliente" (funil real) — clienteId vem do token no backend.
  virouCliente: (leadId, opts = {}) => api.patch(`/captacao/leads/${leadId}/virou-cliente`, opts),
  // CRM/kanban: contagens por estágio + lista; e transição de estágio (perdido leva motivo).
  crm: (params = {}) => api.get('/captacao/crm', { params }),
  mudarEstado: (leadId, estado, motivo_perda) => api.patch(`/captacao/leads/${leadId}/estado`, { estado, motivo_perda }),
};

// IA que ajuda: estado vivo da conversa (consciência/temperatura/objeção) + sugestão de resposta + modo.
export const conversaIAAPI = {
  sugestao: (conversaId) => api.get(`/conversas/sugestao/${conversaId}`),
  setModo: (conversaId, modo) => api.patch(`/conversas/reply-mode/${conversaId}`, { modo }),
  funil: (clienteId) => api.get(`/conversas/funil/${clienteId}`),
};

// Playbook: frameworks de copy editáveis que alimentam a IA (fonte única).
export const playbookAPI = {
  listar: (clienteId) => api.get(`/playbook/${clienteId}`),
  salvar: (skillId, data) => api.patch(`/playbook/skill/${skillId}`, data),
  bloco: (clienteId) => api.get(`/playbook/bloco/${clienteId}`),
  // Puxa o playbook oficial mais novo (re-seed): texto volta ao seed, liga/desliga preservado.
  resemear: (clienteId) => api.post(`/playbook/resemear/${clienteId}`),
};

// A/B honesto: experimentos de copy, avaliação por tendência (nunca p-value).
export const experimentosAPI = {
  listar: (clienteId) => api.get(`/experimentos/${clienteId}`),
  criar: (clienteId, data) => api.post(`/experimentos/${clienteId}`, data),
  avaliar: (experimentId) => api.get(`/experimentos/avaliar/${experimentId}`),
};

// Régua de follow-up: tarefas datadas + leads que sumiram + sequências. NUNCA envia (só rascunho).
export const reguaAPI = {
  tarefas: (clienteId, escopo) => api.get(`/regua/tarefas/${clienteId}`, { params: escopo ? { escopo } : {} }),
  sumiram: (clienteId) => api.get(`/regua/sumiram/${clienteId}`),
  gerarGhosts: (clienteId) => api.post(`/regua/gerar-ghosts/${clienteId}`),
  concluir: (taskId) => api.patch(`/regua/tarefas/${taskId}/concluir`),
  sequencias: (clienteId) => api.get(`/regua/sequencias/${clienteId}`),
  salvarSequencia: (clienteId, data) => api.post(`/regua/sequencias/${clienteId}`, data),
  // O que sairia se o follow-up automático fosse ligado agora (leitura pura — não envia nada).
  previaFollowup: () => api.get('/chips/followup/previa'),
  // Encerra toques velhos demais. Sem `aplicar` é PRÉVIA; nada é apagado (ADR-M5-002).
  expirarVencidas: (clienteId, aplicar) => api.post(`/regua/expirar-vencidas/${clienteId}${aplicar ? '?aplicar=1' : ''}`),
};

// Painel: as métricas vinham de `fetch` cru, sem Authorization e apontando pra outra porta —
// com as rotas protegidas, todo request voltava 401 e o painel mostrava zero. Agora passa pelo
// cliente que injeta o token.
export const dashboardAPI = {
  metricas: (clienteId) => api.get(`/dashboard/metrics/${clienteId}`),
  graficos: (clienteId) => api.get(`/dashboard/charts/${clienteId}`),
  recentes: (clienteId) => api.get(`/dashboard/recent/${clienteId}`),
};

export default api;

// ── Abas da onda 8 ────────────────────────────────────────────────────────────────────────────
// cliente_id NUNCA vai na URL: o backend tira do token (sem IDOR).
export const agentesAPI = {
  // Liga (ou desliga, com agenteId=null) um número a este agente. MESMA escrita que o modal da tela
  // Chips usa — duas portas de entrada, uma fonte só. Em Chips a pergunta é "de quem é este número?";
  // aqui é "quais números falam por este agente?".
  ligarChip: (instanciaId, agenteId) => api.patch(`/agentes/chip/${instanciaId}`, { agente_id: agenteId }),
  // Onda 6/7: abordagens do agente, resultado derivado e o prompt montado.
  abordagens: (id) => api.get(`/agentes/${id}/abordagens`),
  salvarAbordagem: (id, dados) => api.post(`/agentes/${id}/abordagens`, dados),
  semearAbordagens: () => api.post('/agentes/abordagens/semear', {}),
  // Onda 14: a biblioteca de 10 ângulos, o catálogo pra LER, e o liga/desliga por abordagem.
  semearBiblioteca: () => api.post('/agentes/abordagens/biblioteca', {}),
  catalogo: () => api.get('/agentes/abordagens/catalogo'),
  alternar: (abordagemId, ativo) => api.patch(`/agentes/abordagens/${abordagemId}/ativo`, { ativo }),
  previaAbordagem: (id, postura, nome, leadId) =>
    api.get(`/agentes/${id}/abordagens/previa?postura=${postura}&nome=${encodeURIComponent(nome || '')}&lead_id=${leadId || 'previa'}`),
  resultado: (id) => api.get(`/agentes/${id}/resultado`),
  prompt: (id) => api.get(`/agentes/${id}/prompt`),
  listar: () => api.get('/agentes'),
  detalhe: (id) => api.get(`/agentes/${id}`),
  salvar: (id, dados) => api.patch(`/agentes/${id}`, dados),
  criar: (dados) => api.post('/agentes', dados),
};

// `chip` e `agente` são opcionais. O backend responde `filtro_ignorado` nos degraus (estado do lead
// não se corta por chip) e aplica de verdade em /tempo (evento tem instancia_id e agente_id).
const corte = (chip, agente) => {
  const q = [];
  if (chip) q.push(`chip=${encodeURIComponent(chip)}`);
  if (agente) q.push(`agente=${encodeURIComponent(agente)}`);
  return q.length ? `&${q.join('&')}` : '';
};

export const funilAPI = {
  degraus: (chip, agente) => api.get(`/funil/degraus?_=1${corte(chip, agente)}`),
  tempo: (semanas = 12, chip, agente) => api.get(`/funil/tempo?semanas=${semanas}${corte(chip, agente)}`),
  degrau: (chave, pagina = 1) => api.get(`/funil/degrau/${chave}?pagina=${pagina}`),
  // A conta da venda: abordagens/dia → vendas/mês, com intervalo. `meta` traduz reais em abordagens.
  conta: (meta) => api.get(`/funil/conta${meta ? `?meta=${meta}` : ''}`),
};

export const prospeccaoAPI = {
  panorama: (clienteId) => api.get(`/prospeccao/${clienteId}`),
  funis: (clienteId, semanas = 16) => api.get(`/prospeccao/${clienteId}/funis?semanas=${semanas}`),
};

export const metasAPI = {
  painel: () => api.get('/metas'),
  salvar: (dados) => api.post('/metas', dados),
};


// ── Onda 13: plano de abordagem por chip, e a fila que obedece a ele ──────────────────────────
// `cliente_id` nunca vai na URL (vem do token). A prévia e o "montar fila" usam a MESMA alocação no
// backend — prévia que mostra uma coisa e fila que grava outra é decoração com cara de garantia.
export const planoAPI = {
  ver: () => api.get('/plano'),
  salvarChip: (instanciaId, itens) => api.put(`/plano/chip/${instanciaId}`, { itens }),
  teto: (instanciaId, valor) => api.put(`/plano/chip/${instanciaId}/teto`, { valor }),
  previa: (quantos) => api.get(`/plano/previa${quantos ? `?quantos=${quantos}` : ''}`),
  montarFila: (quantos) => api.post('/plano/montar-fila', quantos ? { quantos } : {}),
  fila: (estado) => api.get(`/plano/fila${estado ? `?estado=${estado}` : ''}`),
  ajustarAlvo: (alvoId, dados) => api.patch(`/plano/fila/${alvoId}`, dados),
  /** A aba Operação inteira num payload. `meta` só pra prévia do "quanto cabe". */
  operacao: (meta) => api.get(`/plano/operacao${meta != null && meta !== '' ? `?meta=${meta}` : ''}`),
  resultado: (dias) => api.get(`/plano/resultado?dias=${dias || 7}`),
};
